import {
  IComboBoxOption,
  IconButton,
  IIconProps,
  mergeStyleSets,
} from "@fluentui/react";
import CustomComboBox from "components/shared/constraints/CustomComboBox";
import customTheme from "helpers/customTheme";
import { useGetPolicyConstraintValuesQuery } from "services/template";
import { useState, useEffect, useRef, useMemo } from "react";

const PolicyCriteriaEditListItemStyle = mergeStyleSets({
  valueCombo: {
    input: {
      color: customTheme.secondaryGrey130,
    },
  },
  list: {
    fontSize: 12,
    color: customTheme.secondaryGrey130,
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
    ".constraintName": {
      marginTop: -7,
      width: 200,
      minWidth: 150,
      color: customTheme.bodyColor,
      fontSize: 14,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    ".constraintOperator": {
      marginTop: -7,
      marginRight: 5,
      width: 56,
      textAlign: "center",
    },
    ".constraintValues": {
      marginTop: -7,
      flex: 1,
      marginRight: 10,
    },
    ".remove": {
      textAlign: "center",
      marginRight: 10,
      marginTop: -7,
    },
    ".onlyOne": {
      fontSize: 14,
      marginTop: 7,
      marginLeft: 9,
    },
  },
});

interface IPolicyCriteriaEditPanelOpts extends IComboBoxOption {
  name: string;
}

const constraintOperatorOptions: IPolicyCriteriaEditPanelOpts[] = [
  {
    key: "Include",
    text: "=",
    name: "equals",
  },
  {
    key: "Exclude",
    text: "!=",
    name: "does not equal",
  },
];

export interface IPolicyCriteriaEditListProps {
  constraint: any;
  constraintIndex: number;
  onConstraintUpdated: (
    constraint: any,
    constraintIndex: number,
    isRemoved?: boolean
  ) => void;
  disabled?: boolean;
}

const CancelIcon: IIconProps = {
  iconName: "Cancel",
  styles: { root: { fontSize: 12 } },
};

function PolicyCriteriaEditListItem({
  constraint,
  constraintIndex,
  onConstraintUpdated,
  disabled = false,
}: IPolicyCriteriaEditListProps) {
  const [selectedOperator, setSelectedOperator] = useState<string>(constraint.operator || "Include");

  // Initialize selected values array
  const getInitialValues = () => {
    if (constraint.values && Array.isArray(constraint.values) && constraint.values.length > 0) {
      return constraint.values;
    }
    if (constraint.value && constraint.value !== "") {
      return [constraint.value];
    }
    return [];
  };
  const [selectedValues, setSelectedValues] = useState<string[]>(getInitialValues());

  // Sync selectedValues when constraint changes
  useEffect(() => {
    const newValues = getInitialValues();
    if (JSON.stringify(newValues) !== JSON.stringify(selectedValues)) {
      setSelectedValues(newValues);
    }
  }, [constraint.values, constraint.value]);

  // Fetch constraint values using the new API
  const constraintId = constraint.keyId || constraint.key || constraint.id;
  const { data: constraintValues = [], isLoading: isLoadingValues } = useGetPolicyConstraintValuesQuery(
    constraintId,
    {
      skip: !constraintId,
    }
  );

  // Available values for the constraint - mark selected items
  const availableValues = constraintValues || [];
  const valueOptions: IComboBoxOption[] = useMemo(() => availableValues.map((value: any) => {
    // Use name as the key (actual value), not id (GUID)
    const key = value.name || value.key;
    return {
      key,
      text: value.display || value.name || value.text,
      selected: selectedValues.includes(key),
    };
  }), [availableValues, selectedValues]);

  // Auto-select single value like in template constraint editing
  const hasAutoSelected = useRef(false);
  useEffect(() => {
    if (!hasAutoSelected.current && valueOptions.length === 1 && selectedValues.length === 0) {
      hasAutoSelected.current = true;
      const singleValue = valueOptions[0];
      const updatedValues = [singleValue.key as string];
      const updatedValueDisplays = [singleValue.text];

      setSelectedValues(updatedValues);

      const updatedConstraint = {
        ...constraint,
        values: updatedValues,
        valueDisplays: updatedValueDisplays,
        // Keep backward compatibility
        value: updatedValues[0],
        valueDisplay: updatedValueDisplays[0],
      };

      onConstraintUpdated(updatedConstraint, constraintIndex);
    }
  }, [valueOptions, selectedValues.length, constraint, constraintIndex, onConstraintUpdated]);

  useEffect(() => {
    setSelectedOperator(constraint.operator || "Include");
  }, [constraint.operator]);

  const handleOperatorChange = (option?: IComboBoxOption) => {
    if (option) {
      const newOperator = option.key as string;
      setSelectedOperator(newOperator);

      const updatedConstraint = {
        ...constraint,
        operator: newOperator,
      };

      onConstraintUpdated(updatedConstraint, constraintIndex);
    }
  };

  const handleValueChange = (option?: IComboBoxOption) => {
    if (option) {
      const newValue = option.key as string;
      const newValueDisplay = option.text as string;

      let updatedValues: string[];
      let updatedValueDisplays: string[];

      if (option.selected) {
        // Add to selection - store the key
        updatedValues = [...selectedValues, newValue];
        const currentDisplays = constraint.valueDisplays || [];
        updatedValueDisplays = [...currentDisplays, newValueDisplay];
      } else {
        // Remove from selection - match by key OR by text (display name)
        updatedValues = selectedValues.filter(v => v !== newValue && v !== newValueDisplay);
        const currentDisplays = constraint.valueDisplays || [];
        // Find the index of the item to remove
        const removeIndex = selectedValues.findIndex(v => v === newValue || v === newValueDisplay);
        updatedValueDisplays = currentDisplays.filter((_: string, index: number) => index !== removeIndex);
      }

      setSelectedValues(updatedValues);

      const updatedConstraint = {
        ...constraint,
        values: updatedValues,
        valueDisplays: updatedValueDisplays,
        // Keep backward compatibility
        value: updatedValues[0] || "",
        valueDisplay: updatedValueDisplays[0] || "",
      };

      onConstraintUpdated(updatedConstraint, constraintIndex);
    }
  };

  const handleRemove = () => {
    onConstraintUpdated(constraint, constraintIndex, true);
  };

  const getTooltipTitle = () => {
    if (valueOptions.length === 1) {
      return valueOptions[0]?.text;
    }
    
    if (selectedValues.length > 0) {
      return selectedValues
        .map(v => {
          const option = valueOptions.find(opt => opt.key === v);
          return option?.text;
        })
        .filter(Boolean)
        .join(", ");
    }
    
    return undefined;
  };

  return (
    <div className={PolicyCriteriaEditListItemStyle.list}>
      <div className="constraintName" title={constraint.keyDisplay || constraint.name}>
        {constraint.keyDisplay || constraint.name}
      </div>
      <div className="constraintOperator">
        <CustomComboBox
          options={constraintOperatorOptions}
          defaultKey={[selectedOperator]}
          onOptionSelected={handleOperatorChange}
          disabled={disabled}
          style={{
           // width: "50px",
          }}
        />
      </div>
      <div
        className="constraintValues"
        title={getTooltipTitle()}
      >
        {(() => {
          // Check loading state first - only show on initial load, not refetches
          if (isLoadingValues && availableValues.length === 0) {
            return (
              <div style={{
                padding: "6px 8px",
                fontSize: "12px",
                color: "#666",
                fontStyle: "italic"
              }}>
                Loading values...
              </div>
            );
          }

          // Build combined options: API values + any selected values not in API
          // Mark each option as selected if it's in selectedValues
          // Match by key OR by text (display name) since saved values might use display names
          const optionsToShow: IComboBoxOption[] = valueOptions.map(opt => {
            const isSelected = selectedValues.includes(opt.key as string) || 
                               selectedValues.includes(opt.text as string);
            return {
              ...opt,
              selected: isSelected,
            };
          });
          
          // Build the actual selected keys for the ComboBox (must match option keys)
          const selectedKeys: string[] = [];
          selectedValues.forEach((val) => {
            // Find matching option by key or text
            const matchingOpt = optionsToShow.find(
              opt => opt.key === val || opt.text === val
            );
            if (matchingOpt) {
              selectedKeys.push(matchingOpt.key as string);
            }
          });
          
          // Add any selected values that aren't in the API response
          selectedValues.forEach((val, idx) => {
            const existsInOptions = optionsToShow.find(opt => opt.key === val || opt.text === val);
            if (!existsInOptions) {
              const displayText = constraint.valueDisplays?.[idx] || constraint.valueDisplay || val;
              optionsToShow.push({
                key: val,
                text: displayText,
                selected: true,
              });
              selectedKeys.push(val);
            }
          });

          if (optionsToShow.length === 0) {
            return (
              <div style={{
                padding: "6px 8px",
                fontSize: "12px",
                color: "#666",
                fontStyle: "italic"
              }}>
                No values available
              </div>
            );
          }

          if (optionsToShow.length === 1) {
            // Show single value as plain text, not dropdown
            return (
              <p className="onlyOne">{optionsToShow[0].text ?? ""}</p>
            );
          }

          // Show dropdown with all options (API values + selected values)
          return (
            <CustomComboBox
              options={optionsToShow}
              defaultKey={selectedKeys}
              onOptionSelected={handleValueChange}
              placeholder="Select values..."
              className={PolicyCriteriaEditListItemStyle.valueCombo}
              multiSelect
              disabled={disabled}
            />
          );
        })()}
      </div>
      <div className="remove">
        <IconButton
          data-automation-id="remove-added-constraint"
          iconProps={CancelIcon}
          title="Remove"
          ariaLabel="Remove"
          onClick={handleRemove}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export default PolicyCriteriaEditListItem;